import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { buildApiUrl } from '../config';

const QuickLinksManager = () => {
  const [quickLinks, setQuickLinks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  
  // Form state
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [url, setUrl] = useState('');
  const [imageFile, setImageFile] = useState(null);
  const [credentialsFile, setCredentialsFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  
  // Edit mode state
  const [editMode, setEditMode] = useState(false);
  const [editingId, setEditingId] = useState(null);

  // Load quick links on mount
  useEffect(() => {
    loadQuickLinks();
  }, []);

  const loadQuickLinks = async () => {
    setLoading(true);
    try {
      const response = await axios.get(buildApiUrl('/api/quick-links'));
      if (response.data.success) {
        setQuickLinks(response.data.quick_links);
      }
    } catch (error) {
      console.error('Failed to load quick links:', error);
      alert('Failed to load quick links');
    } finally {
      setLoading(false);
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleCredentialsChange = (e) => {
    const file = e.target.files[0];
    setCredentialsFile(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!name || !url) {
      alert('Please provide at least a name and URL');
      return;
    }

    setUploading(true);
    
    try {
      const formData = new FormData();
      formData.append('name', name);
      formData.append('description', description || ''); // Ensure empty string if not provided
      formData.append('url', url);
      
      if (imageFile) {
        formData.append('image', imageFile);
      }
      
      if (credentialsFile) {
        formData.append('credentials_file', credentialsFile);
      }

      let response;
      if (editMode && editingId) {
        // Update existing link
        response = await axios.put(
          buildApiUrl(`/api/quick-links/${editingId}`),
          formData,
          {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          }
        );
      } else {
        // Create new link
        response = await axios.post(
          buildApiUrl('/api/quick-links'),
          formData,
          {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          }
        );
      }

      if (response.data.success) {
        alert(`Quick link ${editMode ? 'updated' : 'created'} successfully!`);
        // Reset form
        handleCancelEdit();
        // Reload links
        loadQuickLinks();
      } else {
        alert(`Failed to ${editMode ? 'update' : 'create'} quick link: ${response.data.message}`);
      }
    } catch (error) {
      console.error(`Failed to ${editMode ? 'update' : 'create'} quick link:`, error);
      alert(`Failed to ${editMode ? 'update' : 'create'} quick link`);
    } finally {
      setUploading(false);
    }
  };

  const handleEdit = (link) => {
    setEditMode(true);
    setEditingId(link.id);
    setName(link.name);
    setDescription(link.description || '');
    setUrl(link.url);
    setImagePreview(link.image ? buildApiUrl(link.image) : null);
    // Scroll to form
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleCancelEdit = () => {
    setEditMode(false);
    setEditingId(null);
    setName('');
    setDescription('');
    setUrl('');
    setImageFile(null);
    setCredentialsFile(null);
    setImagePreview(null);
  };

  const handleDelete = async (linkId, linkName) => {
    if (!window.confirm(`Are you sure you want to delete "${linkName}"?`)) {
      return;
    }

    try {
      const response = await axios.delete(buildApiUrl(`/api/quick-links/${linkId}`));
      
      if (response.data.success) {
        alert('Quick link deleted successfully!');
        loadQuickLinks();
      } else {
        alert(`Failed to delete quick link: ${response.data.message}`);
      }
    } catch (error) {
      console.error('Failed to delete quick link:', error);
      alert('Failed to delete quick link');
    }
  };

  return (
    <div className="p-6">
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
            <span className="text-white font-bold">🔗</span>
          </div>
          <h1 className="text-3xl font-bold text-gray-900">Quick Links Manager</h1>
        </div>
        <p className="text-gray-600">Manage subscription logins and quick access links</p>
      </div>

      {/* Create/Edit Quick Link Form */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-8">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold text-gray-800">
            {editMode ? 'Edit Quick Link' : 'Create New Quick Link'}
          </h2>
          {editMode && (
            <button
              type="button"
              onClick={handleCancelEdit}
              className="text-gray-600 hover:text-gray-800 px-3 py-1 rounded-lg hover:bg-gray-100 transition"
            >
              Cancel Edit
            </button>
          )}
        </div>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="e.g., Times Tables Rock Stars"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Brief description of the subscription"
              rows="3"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              URL <span className="text-red-500">*</span>
            </label>
            <input
              type="url"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="https://example.com"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Thumbnail Image
            </label>
            <input
              type="file"
              accept="image/*"
              onChange={handleImageChange}
              className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            {imagePreview && (
              <div className="mt-2">
                <img 
                  src={imagePreview} 
                  alt="Preview" 
                  className="w-32 h-32 object-cover rounded-lg border-2 border-gray-300"
                />
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Credentials File (CSV/Excel)
            </label>
            <input
              type="file"
              accept=".csv,.xlsx,.xls"
              onChange={handleCredentialsChange}
              className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <p className="text-xs text-gray-500 mt-1">
              Upload CSV or Excel file with student login credentials. Expected columns: schoolId, firstName, lastName, username, password
            </p>
            {credentialsFile && (
              <div className="mt-2 text-sm text-green-600">
                ✓ Selected: {credentialsFile.name}
              </div>
            )}
          </div>

          <button
            type="submit"
            disabled={uploading}
            className="w-full bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition disabled:bg-gray-400 disabled:cursor-not-allowed font-medium"
          >
            {uploading ? (editMode ? 'Updating...' : 'Creating...') : (editMode ? 'Update Quick Link' : 'Create Quick Link')}
          </button>
        </form>
      </div>

      {/* Existing Quick Links List */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-xl font-bold text-gray-800 mb-4">Existing Quick Links</h2>
        
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <span className="ml-3 text-gray-600">Loading...</span>
          </div>
        ) : quickLinks.length === 0 ? (
          <p className="text-gray-500 text-center py-8">No quick links yet. Create your first one above!</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {quickLinks.map((link) => (
              <div 
                key={link.id} 
                className="border border-gray-200 rounded-lg p-4 hover:shadow-lg transition-shadow"
              >
                {link.image && (
                  <img 
                    src={buildApiUrl(link.image)}
                    alt={link.name}
                    className="w-full h-40 object-cover rounded-lg mb-3"
                  />
                )}
                
                <h3 className="font-bold text-lg text-gray-800 mb-2">{link.name}</h3>
                
                {link.description && (
                  <p className="text-sm text-gray-600 mb-3">{link.description}</p>
                )}
                
                <a 
                  href={link.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-blue-600 hover:text-blue-800 block mb-2 truncate"
                >
                  🔗 {link.url}
                </a>
                
                {link.has_credentials && (
                  <div className="text-xs text-green-600 mb-3">
                    ✓ Credentials: {link.credentials_file}
                  </div>
                )}
                
                <div className="flex gap-2 mt-4">
                  <button
                    onClick={() => handleEdit(link)}
                    className="flex-1 bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 transition text-sm font-medium"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleDelete(link.id, link.name)}
                    className="flex-1 bg-red-500 text-white px-4 py-2 rounded-lg hover:bg-red-600 transition text-sm font-medium"
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default QuickLinksManager;
